import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getLoanMerges,
  addLoanMerge,
  editLoanMerge,
  submitLoanMerge,
  approveLoanMerge,
  rejectLoanMerge,
  deleteLoanMerge,
  startLoadingLoanMerge,
  stopLoadingLoanMerge,
} from './actions'
import { LoanMergeParams } from 'app/models/loan-merge-params'
import { coalesceThunkByKey } from 'app/state/utils/coalesce-thunk'

export const saveLoanMerge =
  (savedLoanMerge: LoanMergeParams, loanMerge?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    if (loanMerge) {
      return client
        .put(`${apiServerUrl}/api/draft-loan-merges`, savedLoanMerge)
        .then(response => {
          const payload = dispatch(editLoanMerge(response))
          return { success: true, payload: payload.payload }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    }

    return client
      .post(`${apiServerUrl}/api/draft-loan-merges`, savedLoanMerge)
      .then(response => {
        const payload = dispatch(addLoanMerge(response))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const loadLoanMergeId = (loanMergeId: string) => async () => {
  const apiServerUrl = config.backendUrl
  return client
    .get(
      `${apiServerUrl}/api/approved-loan-merges/one?id=${loanMergeId}`,
    )
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}
export const loadApprovedLoanMerges = async (loanId: string | undefined) => {
  const apiServerUrl = config.backendUrl
  if (loanId) {
    return await client
      .get(`${apiServerUrl}/api/approved-loan-merges/loan?id=${loanId}`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  } else {
    return await client
      .get(`${apiServerUrl}/api/approved-loan-merges`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  }
}

export const loadDraftLoanMerges = async (loanId: string | undefined) => {
  const apiServerUrl = config.backendUrl
  if (loanId) {
    return await client
      .get(`${apiServerUrl}/api/draft-loan-merges/loan?id=${loanId}`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  } else {
    return await client
      .get(`${apiServerUrl}/api/draft-loan-merges`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  }
}

export const loadSubmittedLoanMerges = async (loanId: string | undefined) => {
  const apiServerUrl = config.backendUrl
  if (loanId) {
    return await client
      .get(`${apiServerUrl}/api/submitted-loan-merges/loan?id=${loanId}`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  } else {
    return await client
      .get(`${apiServerUrl}/api/submitted-loan-merges`)
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  }
}

export const submitForApproval =
  (loanMerge: LoanMergeParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/draft-loan-merges/submit`, {
        id: loanMerge.id,
      })
      .then(response => {
        const payload = dispatch(submitLoanMerge(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const approveSelectedLoanMerge =
  (loanMerge: LoanMergeParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-loan-merges/approve`, {
        id: loanMerge.id,
      })
      .then(response => {
        const payload = dispatch(approveLoanMerge(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        console.log(e)
        return { success: false, payload: e.response.data.error }
      })
  }

export const rejectSelectedLoanMerge =
  (loanMerge: LoanMergeParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-loan-merges/reject`, {
        id: loanMerge.id,
      })
      .then(response => {
        const payload = dispatch(rejectLoanMerge(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const deleteSelectedLoanMerge =
  (loanMerge: LoanMergeParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(`${apiServerUrl}/api/draft-loan-merges?ids[]=${loanMerge.id}`)
      .then(() => {
        const payload = dispatch(deleteLoanMerge(loanMerge))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const loadAllLoanMerges = coalesceThunkByKey(
  (loanId?: string | undefined) => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingLoanMerge())
      let all_loan_merges: any[] = []

      const draftLoanMerges = await loadDraftLoanMerges(loanId)
      const submittedLoanMerges = await loadSubmittedLoanMerges(loanId)

      const arr = submittedLoanMerges.map(
        (a: { loanMerge: LoanMergeParams }) => a.loanMerge,
      )
      const sub_loan_merges = arr.map((a: LoanMergeParams) => ({
        ...a,
        status: 'Submitted',
      }))

      all_loan_merges = draftLoanMerges
        .map((loanMerge: LoanMergeParams) => ({
          ...loanMerge,
          status: 'Draft',
        }))
        .concat(sub_loan_merges)

      return dispatch(getLoanMerges(all_loan_merges))
    } finally {
      dispatch(stopLoadingLoanMerge())
    }
  },
)
